import { abweisen, ereignis, verlangen, type Ereignis, type GespeichertesEreignis } from "../gemeinsam/index.js";
import { E } from "./ereignisse.js";

/**
 * Aggregat Stand (Domänenmodell §4, Wertobjekte Standnummer, Standname, Geoposition, Standposition, Koordinateneingabe,
 * RoteZone).
 */
export const STANDARTEN = ["Drückjagdbock", "Bodenstand", "Leiter", "Kanzel"] as const;
export type Standart = (typeof STANDARTEN)[number];
export type Standstatus = "in Betrieb" | "stillgelegt";
export type Positionsquelle = "GPS-Messung vor Ort" | "Koordinateneingabe";

/** Genauigkeit, bis zu der eine GPS-Position ohne Warnung bzw. ohne ausdrückliche Bestätigung übernommen wird (Q19). */
export const GENAUIGKEIT_OHNE_WARNUNG_M = 10;
export const GENAUIGKEIT_OHNE_BESTAETIGUNG_M = 25;

export interface RoteZone {
  Bezeichnung: string;
  von: number;
  bis: number;
}

export interface StandZustand {
  standId: string | null;
  standnummer: string | null;
  standname: string | null;
  breite: number | null;
  laenge: number | null;
  status: Standstatus;
  roteZonen: RoteZone[];
  standart: string | null;
  zuweg: string | null;
}

export const KEIN_STAND: StandZustand = {
  standId: null, standnummer: null, standname: null, breite: null, laenge: null, status: "in Betrieb", roteZonen: [], standart: null, zuweg: null,
};

export function standAnwenden(z: StandZustand, e: GespeichertesEreignis): StandZustand {
  const d = e.daten;
  switch (e.typ) {
    case E.StandAngelegt:
      return {
        ...z, standnummer: String(d["Standnummer"]), standname: (d["Standname"] as string | null | undefined) ?? null, breite: Number(d["Breite"]), laenge: Number(d["Länge"]),
        status: (d["Standstatus"] as Standstatus | undefined) ?? "in Betrieb",
        standart: (d["Standart"] as string | null | undefined) ?? null, zuweg: (d["Zuweg"] as string | null | undefined) ?? null,
      };
    case E.StandVerlegt:
      return { ...z, breite: Number(d["Breite"]), laenge: Number(d["Länge"]) };
    case E.StandUmbenannt:
      return { ...z, standnummer: String(d["Standnummer"]) };
    case E.StandnameGeaendert:
      return { ...z, standname: (d["Standname"] as string | null | undefined) ?? null };
    case E.StandStillgelegt:
      return { ...z, status: "stillgelegt" };
    case E.RoteZoneFestgelegt:
      return {
        ...z,
        roteZonen: [...z.roteZonen.filter((r) => r.Bezeichnung !== d["Bezeichnung"]),
          { Bezeichnung: String(d["Bezeichnung"]), von: Number(d["von"]), bis: Number(d["bis"]) }],
      };
    case E.RoteZoneEntfernt:
      return { ...z, roteZonen: z.roteZonen.filter((r) => r.Bezeichnung !== d["Bezeichnung"]) };
    default:
      return z;
  }
}

// -- Wertobjekte ---------------------------------------------------------------------------------------------------

const STANDNUMMER = /^([A-Z]?)(\d+)([a-z]?)$/;

/**
 * Standnummer: Ziffern, optional gefolgt von einem Kleinbuchstaben (`17`, `7a`), optional mit einem Großbuchstaben
 * davor (`B3`, `B01`: Stände eines beteiligten Reviers in den Feature Files, z. B. `nachrichten/03`, `lage/04`).
 */
export function standnummerPruefen(wert: unknown): string {
  const t = typeof wert === "string" ? wert.trim() : typeof wert === "number" && Number.isInteger(wert) && wert >= 0 ? String(wert) : "";
  if (!STANDNUMMER.test(t)) abweisen("standnummer-ungueltig");
  return t;
}

/** Dieselbe Form wie {@link standnummerPruefen}, ohne Abweisung: für die Prüfung schon bei der Eingabe (K106 (2)). */
export function standnummerGueltig(eingabe: string): boolean {
  return STANDNUMMER.test(eingabe.trim());
}

/** Höchstlänge des Standnamens in Zeichen (K106 (3)). */
export const STANDNAME_HOECHSTENS = 40;

/**
 * Standname: optionaler Freitext (z. B. „Eichenkanzel“), höchstens 40 Zeichen nach dem Entfernen der Leerzeichen am Rand;
 * fehlt er oder ist er leer, hat der Stand keinen Namen (`null`).
 */
export function standnamePruefen(wert: unknown): string | null {
  if (wert === undefined || wert === null) return null;
  if (typeof wert !== "string") abweisen("unzulaessige-angaben");
  const t = wert.trim();
  if ([...t].length > STANDNAME_HOECHSTENS) abweisen("standname-zu-lang");
  return t || null;
}

/** Anzeige eines Standes (Vertrag oberflaeche.md §7, V-30): `Stand <Nr> · <Standname>`, ohne Standname `Stand <Nr>`. */
export function standBezeichnung(nummer: string, standname: string | null | undefined): string {
  return standname ? `Stand ${nummer} · ${standname}` : `Stand ${nummer}`;
}

/** Vergleichsform: ohne führende Nullen (`017` = `17`, `07a` = `7a`). */
export function standnummerSchluessel(nummer: string): string {
  const m = STANDNUMMER.exec(nummer);
  if (!m) return nummer;
  return `${m[1]}${String(Number(m[2]))}${m[3]}`;
}

/** Sechs Nachkommastellen (N-48). */
export function gerundet(grad: number): number {
  return Math.round(grad * 1e6) / 1e6;
}

export interface Geoposition {
  breite: number;
  laenge: number;
}

function imWertebereich(p: Geoposition): boolean {
  return Number.isFinite(p.breite) && Number.isFinite(p.laenge) && Math.abs(p.breite) <= 90 && Math.abs(p.laenge) <= 180;
}

const DEZIMAL = /^\s*([+-]?\d+(?:\.\d+)?)\s*,\s*([+-]?\d+(?:\.\d+)?)\s*$/;
const GMS_TEIL = String.raw`(\d+)\s*°\s*(\d+)\s*['′]\s*(\d+(?:\.\d+)?)\s*(?:"|″|'')\s*`;
const GMS = new RegExp(String.raw`^\s*${GMS_TEIL}([NS])\s*,?\s*${GMS_TEIL}([EOW])\s*$`, "i");

function gms(grad: string, min: string, sek: string): number | null {
  const m = Number(min);
  const s = Number(sek);
  if (m >= 60 || s >= 60) return null;
  return Number(grad) + m / 60 + s / 3600;
}

/**
 * Koordinateneingabe in den Formaten aus Google Maps (Dezimalgrad `52.921750, 10.341854`, Grad/Minuten/Sekunden
 * `52°55'18.3"N 10°20'30.7"E`) → Geoposition in Dezimalgrad mit sechs Nachkommastellen. Unbekanntes Format, Minuten
 * oder Sekunden ab 60 und Werte außerhalb ±90°/±180° → `position-ungueltig`.
 */
export function koordinatenLesen(eingabe: unknown): Geoposition {
  const t = typeof eingabe === "string" ? eingabe : "";
  let p: Geoposition | null = null;
  const d = DEZIMAL.exec(t);
  if (d) {
    p = { breite: Number(d[1]), laenge: Number(d[2]) };
  } else {
    const g = GMS.exec(t);
    if (g) {
      const b = gms(g[1]!, g[2]!, g[3]!);
      const l = gms(g[5]!, g[6]!, g[7]!);
      if (b !== null && l !== null) {
        p = { breite: g[4]!.toUpperCase() === "S" ? -b : b, laenge: g[8]!.toUpperCase() === "W" ? -l : l };
      }
    }
  }
  if (!p || !imWertebereich(p)) abweisen("position-ungueltig");
  return { breite: gerundet(p.breite), laenge: gerundet(p.laenge) };
}

export function positionPruefen(breite: unknown, laenge: unknown): Geoposition {
  if (typeof breite !== "number" || typeof laenge !== "number") abweisen("position-ungueltig");
  const p = { breite, laenge };
  if (!imWertebereich(p)) abweisen("position-ungueltig");
  return { breite: gerundet(breite), laenge: gerundet(laenge) };
}

/** Genauigkeit einer GPS-Messung: Pflicht, nicht negativ; schlechter als 25 m nur mit ausdrücklicher Bestätigung. */
export function genauigkeitPruefen(wert: unknown, bestaetigt: unknown): number {
  if (wert === undefined || wert === null || wert === "") abweisen("genauigkeit-fehlt");
  if (typeof wert !== "number" || !Number.isFinite(wert) || wert < 0) abweisen("genauigkeit-ungueltig");
  if (wert > GENAUIGKEIT_OHNE_BESTAETIGUNG_M && bestaetigt !== true) abweisen("genauigkeit-nicht-bestaetigt");
  return wert;
}

export function standartPruefen(wert: unknown): Standart | null {
  if (wert === undefined || wert === null || wert === "") return null;
  if (!STANDARTEN.includes(wert as Standart)) abweisen("standart-ungueltig");
  return wert as Standart;
}

function zuwegPruefen(wert: unknown): string | null {
  if (wert === undefined || wert === null) return null;
  if (typeof wert !== "string") abweisen("unzulaessige-angaben");
  const t = wert.trim();
  if (t.length > 500) abweisen("unzulaessige-angaben");
  return t || null;
}

/** Richtung einer roten Zone: ganze Grad im Uhrzeigersinn, 0° = Nord, gültig 0° bis 359° (360° wird abgewiesen). */
export function richtungPruefen(wert: unknown): number {
  if (typeof wert !== "number" || !Number.isInteger(wert) || wert < 0 || wert >= 360) abweisen("richtung-ungueltig");
  return wert;
}

// -- Befehle -------------------------------------------------------------------------------------------------------

/** Standnummer ist unter den Ständen in Betrieb des Reviers eindeutig (F-027); `andere` = alle übrigen Stände. */
export function standnummerFrei(nummer: string, andere: readonly StandZustand[]): void {
  const schluessel = standnummerSchluessel(nummer);
  const vergeben = andere.some((s) => s.status === "in Betrieb" && s.standnummer !== null && standnummerSchluessel(s.standnummer) === schluessel);
  if (vergeben) abweisen("standnummer-vergeben");
}

export interface StandVorOrt {
  Standnummer?: unknown; Breite?: unknown; Länge?: unknown; Genauigkeit?: unknown; "Genauigkeit bestätigt"?: unknown;
  Erfassungszeitpunkt?: unknown; Standart?: unknown; Zuweg?: unknown; Standname?: unknown;
}

function zeitpunkt(wert: unknown, sonst: Date): Date {
  if (typeof wert === "string" && wert) {
    const d = new Date(wert);
    if (Number.isNaN(d.getTime())) abweisen("unzulaessige-angaben");
    return d;
  }
  return sonst;
}

export function standVorOrtAnlegen(d: StandVorOrt, andere: readonly StandZustand[], handelnd: string, erfasst: Date): Ereignis {
  const nummer = standnummerPruefen(d.Standnummer);
  const p = positionPruefen(d.Breite, d["Länge"]);
  const genauigkeit = genauigkeitPruefen(d.Genauigkeit, d["Genauigkeit bestätigt"]);
  const standart = standartPruefen(d.Standart);
  const zuweg = zuwegPruefen(d.Zuweg);
  const standname = standnamePruefen(d.Standname);
  standnummerFrei(nummer, andere);
  return ereignis(E.StandAngelegt, {
    Standnummer: nummer, Standname: standname, Breite: p.breite, "Länge": p.laenge, Genauigkeit: genauigkeit, Positionsquelle: "GPS-Messung vor Ort",
    Erfassungszeitpunkt: zeitpunkt(d.Erfassungszeitpunkt, erfasst), Standart: standart, Zuweg: zuweg, Standstatus: "in Betrieb",
    "handelnde Person": handelnd,
  });
}

export interface StandMitKoordinaten { Standnummer?: unknown; Koordinaten?: unknown; Standart?: unknown; Zuweg?: unknown; Standname?: unknown }

export function standMitKoordinatenAnlegen(d: StandMitKoordinaten, andere: readonly StandZustand[], handelnd: string, jetzt: Date): Ereignis {
  const nummer = standnummerPruefen(d.Standnummer);
  const p = koordinatenLesen(d.Koordinaten);
  const standart = standartPruefen(d.Standart);
  const zuweg = zuwegPruefen(d.Zuweg);
  const standname = standnamePruefen(d.Standname);
  standnummerFrei(nummer, andere);
  return ereignis(E.StandAngelegt, {
    Standnummer: nummer, Standname: standname, Breite: p.breite, "Länge": p.laenge, Genauigkeit: null, Positionsquelle: "Koordinateneingabe",
    Erfassungszeitpunkt: jetzt, Standart: standart, Zuweg: zuweg, Standstatus: "in Betrieb", "handelnde Person": handelnd,
  });
}

function inBetrieb(z: StandZustand): void {
  verlangen(z.status === "in Betrieb", "stand-stillgelegt");
}

export interface StandVerlegung {
  Koordinaten?: unknown; Breite?: unknown; Länge?: unknown; Genauigkeit?: unknown; "Genauigkeit bestätigt"?: unknown;
  Erfassungszeitpunkt?: unknown;
}

export function standVerlegen(z: StandZustand, d: StandVerlegung, handelnd: string, jetzt: Date): Ereignis[] {
  inBetrieb(z);
  if (d.Koordinaten !== undefined) {
    const p = koordinatenLesen(d.Koordinaten);
    return [ereignis(E.StandVerlegt, {
      Breite: p.breite, "Länge": p.laenge, Genauigkeit: null, Positionsquelle: "Koordinateneingabe", Erfassungszeitpunkt: jetzt,
      "handelnde Person": handelnd,
    })];
  }
  const p = positionPruefen(d.Breite, d["Länge"]);
  const genauigkeit = genauigkeitPruefen(d.Genauigkeit, d["Genauigkeit bestätigt"]);
  return [ereignis(E.StandVerlegt, {
    Breite: p.breite, "Länge": p.laenge, Genauigkeit: genauigkeit, Positionsquelle: "GPS-Messung vor Ort",
    Erfassungszeitpunkt: zeitpunkt(d.Erfassungszeitpunkt, jetzt), "handelnde Person": handelnd,
  })];
}

export function standUmbenennen(z: StandZustand, neu: unknown, andere: readonly StandZustand[], handelnd: string): Ereignis[] {
  inBetrieb(z);
  const nummer = standnummerPruefen(neu);
  if (z.standnummer === nummer) return [];
  standnummerFrei(nummer, andere);
  return [ereignis(E.StandUmbenannt, { Standnummer: nummer, "handelnde Person": handelnd })];
}

/** Standname ändern oder mit leerem bzw. fehlendem Namen entfernen (K106 (3)); unverändert ergibt kein Ereignis. */
export function standnameAendern(z: StandZustand, neu: unknown, handelnd: string): Ereignis[] {
  inBetrieb(z);
  const standname = standnamePruefen(neu);
  if (z.standname === standname) return [];
  return [ereignis(E.StandnameGeaendert, { Standname: standname, "handelnde Person": handelnd })];
}

export function standStilllegen(z: StandZustand, handelnd: string): Ereignis[] {
  if (z.status === "stillgelegt") return [];
  return [ereignis(E.StandStillgelegt, { "handelnde Person": handelnd })];
}

export function roteZoneFestlegen(z: StandZustand, d: { Bezeichnung?: unknown; von?: unknown; bis?: unknown }, handelnd: string): Ereignis[] {
  inBetrieb(z);
  const bezeichnung = typeof d.Bezeichnung === "string" ? d.Bezeichnung.trim() : "";
  if (!bezeichnung) abweisen("bezeichnung-fehlt");
  if (bezeichnung.length > 80) abweisen("unzulaessige-angaben");
  const von = richtungPruefen(d.von);
  const bis = richtungPruefen(d.bis);
  const alt = z.roteZonen.find((r) => r.Bezeichnung === bezeichnung);
  if (alt && alt.von === von && alt.bis === bis) return [];
  return [ereignis(E.RoteZoneFestgelegt, { Bezeichnung: bezeichnung, von, bis, "handelnde Person": handelnd })];
}

export function roteZoneEntfernen(z: StandZustand, bezeichnung: unknown, handelnd: string): Ereignis[] {
  const b = typeof bezeichnung === "string" ? bezeichnung.trim() : "";
  if (!b) abweisen("bezeichnung-fehlt");
  if (!z.roteZonen.some((r) => r.Bezeichnung === b)) return [];
  return [ereignis(E.RoteZoneEntfernt, { Bezeichnung: b, "handelnde Person": handelnd })];
}

// -- Plausibilität der Koordinateneingabe (F-023, K21: nur in der App) --------------------------------------------

/** Entfernung zweier Positionen in km (Haversine, Erdradius 6371 km). */
export function entfernungKm(a: Geoposition, b: Geoposition): number {
  const rad = Math.PI / 180;
  const db = (b.breite - a.breite) * rad;
  const dl = (b.laenge - a.laenge) * rad;
  const h = Math.sin(db / 2) ** 2 + Math.cos(a.breite * rad) * Math.cos(b.breite * rad) * Math.sin(dl / 2) ** 2;
  return 2 * 6371 * Math.asin(Math.min(1, Math.sqrt(h)));
}

export function naechsteEntfernungKm(p: Geoposition, staende: readonly Geoposition[]): number | null {
  if (staende.length === 0) return null;
  return Math.min(...staende.map((s) => entfernungKm(p, s)));
}

/**
 * Untere Schranke für den Wortlaut „mehr als <n> km“ (oberflaeche.md §9): die größte der Stufen Warnschwelle, 4, 40,
 * 400, 4000 km, die die Entfernung erreicht (44 km → 40, 6066 km → 4000).
 */
export function entfernungAbgerundet(km: number, warnungKm: number): number {
  return Math.max(warnungKm, ...[4, 40, 400, 4000].filter((s) => s <= km));
}

export interface Plausibilitaet {
  warnung: boolean;
  entfernungKm: number | null;
  vertauscht: boolean;
}

/** Warnung ab `warnungKm` zum nächsten Stand; Hinweis auf Vertauschung, wenn die getauschte Position näher als `tauschKm` läge. */
export function plausibilitaet(p: Geoposition, staende: readonly Geoposition[], warnungKm: number, tauschKm: number): Plausibilitaet {
  const e = naechsteEntfernungKm(p, staende);
  if (e === null || e < warnungKm) return { warnung: false, entfernungKm: e, vertauscht: false };
  const getauscht = { breite: p.laenge, laenge: p.breite };
  const t = Math.abs(getauscht.breite) <= 90 ? naechsteEntfernungKm(getauscht, staende) : null;
  return { warnung: true, entfernungKm: e, vertauscht: t !== null && t < tauschKm };
}
